import { createRequest } from '../helpers/api_utils';
import { AddToFolderResult, CandiBankFolder } from '../types/candi_bank_types';
import { apiUrl } from './api_url';

const apiEndpoint = `${apiUrl}/candi-bank/folders`;

/** Error carrying the server's message (e.g. the 409 duplicate-name text) for a toast. */
export class FolderApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'FolderApiError';
  }
}

async function call<T>(request: Request): Promise<T> {
  const response = await fetch(request);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = body?.detail ?? body?.error ?? body?.message ?? `Request failed (${response.status})`;
    throw new FolderApiError(typeof message === 'string' ? message : JSON.stringify(message), response.status);
  }
  return body as T;
}

// GET /candi-bank/folders
export async function listFolders(): Promise<CandiBankFolder[]> {
  const body = await call<{ folders: CandiBankFolder[] }>(
    createRequest(apiEndpoint, 'GET', 'application/json', true),
  );
  return body.folders;
}

// POST /candi-bank/folders  -> 409 when the name is taken (case-insensitive)
export async function createFolder(name: string, jobOpeningId?: string | null): Promise<CandiBankFolder> {
  const body = await call<{ folder: CandiBankFolder }>(
    createRequest(apiEndpoint, 'POST', 'application/json', true, { name, jobOpeningId: jobOpeningId || null }),
  );
  return body.folder;
}

// PATCH /candi-bank/folders/{id}  -- send only what changes; jobOpeningId: null unlinks
export async function updateFolder(
  folderId: string,
  changes: { name?: string; jobOpeningId?: string | null },
): Promise<CandiBankFolder> {
  const body = await call<{ folder: CandiBankFolder }>(
    createRequest(`${apiEndpoint}/${folderId}`, 'PATCH', 'application/json', true, changes),
  );
  return body.folder;
}

// DELETE /candi-bank/folders/{id}  -> 403 unless creator or super admin
export async function deleteFolder(folderId: string): Promise<void> {
  await call(createRequest(`${apiEndpoint}/${folderId}`, 'DELETE', 'application/json', true));
}

// POST /candi-bank/folders/{id}/candidates
export function addToFolder(folderId: string, jobApplicantIds: string[]): Promise<AddToFolderResult> {
  return call<AddToFolderResult>(
    createRequest(`${apiEndpoint}/${folderId}/candidates`, 'POST', 'application/json', true, { jobApplicantIds }),
  );
}

// DELETE /candi-bank/folders/{id}/candidates
export function removeFromFolder(folderId: string, emails: string[]): Promise<{ removed: number }> {
  return call<{ removed: number }>(
    createRequest(`${apiEndpoint}/${folderId}/candidates`, 'DELETE', 'application/json', true, { emails }),
  );
}
